import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, ArrowRight, Truck, ShieldCheck, PackageCheck, ChevronRight, PackageSearch } from 'lucide-react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { useCart } from '@/contexts/CartContext';
import { useCurrency } from '@/contexts/CurrencyContext';
import DualPrice, { CompactDualPrice } from '@/components/DualPrice';
import { toast } from 'sonner';
import { trackViewContent, trackAddToCart } from '@/components/TrackingPixels';
import { TiltGallery } from '@/components/health/TiltGallery';
import { QuantityStepper, BurstAddButton, SuccessCheck } from '@/components/health/ProductInteractions';
import { InfoTabs } from '@/components/health/InfoTabs';
import { HealthProductCard } from '@/components/health/HealthProductCard';
import { getProductImage, categoryUrl } from '@/lib/health';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08, delayChildren: 0.15 } } };
const item = { hidden: { opacity: 0, y: 22 }, show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } } };

const TRUST = [
  { icon: Truck, title: 'Tracked delivery', desc: 'Updates by email', color: 'bg-health-sky text-sky-700' },
  { icon: ShieldCheck, title: 'Genuine & sealed', desc: 'Over-the-counter', color: 'bg-health-mint text-health-emerald' },
  { icon: PackageCheck, title: 'Discreet packing', desc: 'Plain outer box', color: 'bg-health-peach text-health-coral' },
];

const TrustRow = () => (
  <div className="grid grid-cols-3 gap-3" data-testid="shipping-info">
    {TRUST.map((t) => (
      <motion.div key={t.title} whileHover={{ y: -4, rotate: [0, -2, 2, 0] }} className="bg-white rounded-2xl border border-health-line p-3 sm:p-4 text-center">
        <span className={`w-10 h-10 mx-auto mb-2 rounded-xl ${t.color} flex items-center justify-center`}><t.icon className="h-5 w-5" /></span>
        <p className="font-heading text-sm font-semibold text-health-ink leading-tight">{t.title}</p>
        <p className="text-[11px] text-slate-500 mt-0.5">{t.desc}</p>
      </motion.div>
    ))}
  </div>
);

const ChipGroup = ({ label, value, options, onSelect, isAvailable, testPrefix }) => (
  <div>
    <p className="text-sm font-semibold text-slate-600 mb-3">{label}: <span className="text-health-ink">{value || 'Select'}</span></p>
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const ok = isAvailable(opt);
        const active = value === opt;
        return (
          <motion.button key={opt} whileTap={ok ? { scale: 0.92 } : {}} onClick={() => onSelect(opt)} disabled={!ok}
            className={`relative px-5 py-2.5 rounded-full text-sm font-semibold border-2 transition-colors ${active ? 'border-health-emerald text-white' : ok ? 'border-health-line bg-white text-health-ink hover:border-health-emerald' : 'border-health-line bg-slate-50 text-slate-400 line-through cursor-not-allowed'}`}
            data-testid={`${testPrefix}-${opt.toLowerCase().replace(/\s+/g, '-')}`}>
            {active && <motion.span layoutId={`${testPrefix}-active`} className="absolute inset-0 rounded-full bg-health-emerald" transition={{ type: 'spring', stiffness: 400, damping: 30 }} />}
            <span className="relative">{opt}</span>
          </motion.button>
        );
      })}
    </div>
  </div>
);

const CartModal = ({ open, onClose, product, quantity, selection, total, onCheckout }) => (
  <AnimatePresence>
    {open && (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-health-ink/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
        <motion.div initial={{ scale: 0.85, y: 40, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.9, y: 20, opacity: 0 }} transition={{ type: 'spring', stiffness: 260, damping: 22 }}
          className="bg-white rounded-[2rem] p-7 max-w-md w-full shadow-lift" onClick={(e) => e.stopPropagation()} data-testid="cart-modal">
          <SuccessCheck />
          <h2 className="text-2xl font-heading font-bold text-center text-health-ink mb-1">Added to your bag!</h2>
          <p className="text-slate-500 text-center mb-6">{product.name} × {quantity}{selection && <span className="block text-sm mt-1">{selection}</span>}</p>
          <div className="flex items-center gap-4 p-3 bg-health-mint/60 rounded-2xl mb-6">
            <img src={getProductImage(product)} alt={product.name} className="w-20 h-20 object-cover rounded-xl" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-health-ink line-clamp-2">{product.name}</p>
              <DualPrice amount={total} className="text-lg font-bold text-health-ink" />
            </div>
          </div>
          <div className="space-y-3">
            <Button onClick={onCheckout} className="w-full h-13 py-6 rounded-full bg-health-emerald hover:bg-health-emerald-dark text-white text-base font-bold" data-testid="buy-now-btn">
              <ShoppingBag className="w-5 h-5 mr-2" /> Checkout now <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button onClick={onClose} variant="outline" className="w-full py-6 rounded-full text-base font-bold border-2 border-health-line text-health-ink hover:bg-health-mint" data-testid="continue-shopping-btn">
              Continue shopping
            </Button>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

const NotFound = () => (
  <div className="min-h-screen bg-background">
    <Navbar />
    <div className="max-w-xl mx-auto px-6 pt-40 pb-24 text-center" data-testid="product-not-found">
      <motion.div animate={{ y: [0, -10, 0] }} transition={{ duration: 3, repeat: Infinity }} className="w-20 h-20 mx-auto mb-6 rounded-3xl bg-health-mint flex items-center justify-center">
        <PackageSearch className="h-10 w-10 text-health-emerald" />
      </motion.div>
      <h1 className="font-heading text-3xl font-bold text-health-ink mb-3">This product isn't on our shelves</h1>
      <p className="text-slate-600 mb-8">It may have been moved or is no longer available.</p>
      <Link to="/products"><Button className="rounded-full h-12 px-8 bg-health-emerald hover:bg-health-emerald-dark text-white font-bold">Browse products</Button></Link>
    </div>
    <Footer />
  </div>
);

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [showCartModal, setShowCartModal] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const { addToCart } = useCart();
  const { formatLocalPrice } = useCurrency();
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/products/${id}`);
        const p = response.data;
        setProduct(p);
        setSelectedColor(p.available_colors?.[0] || null);
        setSelectedSize(p.available_sizes?.[0] || null);
        trackViewContent(p);
        axios.post(`${API}/analytics/product-view/${id}`).catch(() => {});
        axios.get(`${API}/products?category=${encodeURIComponent(p.category)}`).then((r) => setRelated(r.data.filter((x) => x.id !== p.id).slice(0, 4))).catch(() => {});
      } catch (error) {
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    if (product?.has_variants && product?.variants?.length > 0) {
      const variant = product.variants.find((v) => (!product.available_colors?.length || v.color === selectedColor) && (!product.available_sizes?.length || v.size === selectedSize));
      setSelectedVariant(variant || null);
    }
  }, [selectedColor, selectedSize, product]);

  const currentPrice = product?.has_variants && selectedVariant ? selectedVariant.price : product?.price || 0;
  const currentStock = product?.has_variants && selectedVariant ? selectedVariant.stock : product?.stock || 0;
  const selection = [selectedColor, selectedSize].filter(Boolean).join(' / ');

  const handleAddToCart = async () => {
    if (product?.has_variants) {
      if (!selectedVariant) return toast.error('This option is not available');
      if (selectedVariant.stock < quantity) return toast.error('Not enough stock for this option');
    }
    setAddingToCart(true);
    await addToCart(product.id, quantity, { variant_id: selectedVariant?.id, color: selectedColor, size: selectedSize });
    trackAddToCart(product, quantity);
    axios.post(`${API}/analytics/add-to-cart/${product.id}`).catch(() => {});
    setAddingToCart(false);
    setShowCartModal(true);
  };

  const handleQuickBuy = async () => {
    setAddingToCart(true);
    await addToCart(product.id, 1, { variant_id: selectedVariant?.id, color: selectedColor, size: selectedSize });
    setAddingToCart(false);
    navigate('/checkout');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center pt-48 pb-24"><div className="spinner" data-testid="product-loading" /></div>
      </div>
    );
  }
  if (!product) return <NotFound />;

  const images = product.images?.length > 0 ? product.images : product.image_url ? [product.image_url] : [];
  const discount = product.original_price && product.original_price > currentPrice ? Math.round((1 - currentPrice / product.original_price) * 100) : 0;

  return (
    <div className="min-h-screen bg-background font-body text-health-ink">
      <Navbar />
      <CartModal open={showCartModal} onClose={() => setShowCartModal(false)} product={product} quantity={quantity} selection={selection} total={currentPrice * quantity} onCheckout={() => { setShowCartModal(false); navigate('/checkout'); }} />

      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pt-28 pb-16">
        <motion.nav initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-1.5 text-sm text-slate-500 mb-8 flex-wrap" data-testid="product-breadcrumb">
          <Link to="/" className="hover:text-health-emerald">Home</Link>
          <ChevronRight className="h-4 w-4" />
          <Link to={categoryUrl(product.category)} className="hover:text-health-emerald">{product.category}</Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-health-ink font-semibold line-clamp-1">{product.name}</span>
        </motion.nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="lg:sticky lg:top-28 self-start px-2 md:px-6">
            <TiltGallery images={images} videoUrl={product.video_url} name={product.name} discount={discount} />
            <div className="lg:hidden flex justify-center mt-4">
              <Button onClick={handleQuickBuy} disabled={addingToCart} size="sm" className="rounded-full bg-health-coral hover:bg-health-coral/90 text-white px-6 font-bold" data-testid="mobile-buy-now">
                {addingToCart ? '...' : 'Buy now'}
              </Button>
            </div>
          </motion.div>

          <motion.div variants={stagger} initial="hidden" animate="show" className="flex flex-col gap-7">
            <motion.div variants={item}>
              <Link to={categoryUrl(product.category)} className="inline-flex items-center gap-2 bg-health-mint text-health-emerald text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full hover:bg-health-emerald hover:text-white transition-colors" data-testid="product-category-link">
                <ShieldCheck className="h-3.5 w-3.5" /> {product.category}
              </Link>
            </motion.div>

            <motion.h1 variants={item} className="font-heading text-4xl sm:text-5xl font-bold leading-tight text-health-ink" data-testid="product-title">
              {product.name}
            </motion.h1>

            <motion.div variants={item} className="flex items-center gap-4 flex-wrap">
              <AnimatePresence mode="wait">
                <motion.div key={currentPrice} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}>
                  <CompactDualPrice amount={currentPrice} originalAmount={product.original_price} className="text-3xl font-heading font-bold text-health-ink" data-testid="product-price" />
                </motion.div>
              </AnimatePresence>
              {discount > 0 && (
                <motion.span animate={{ scale: [1, 1.08, 1] }} transition={{ duration: 1.6, repeat: Infinity }} className="bg-health-coral text-white px-3 py-1 text-sm font-bold rounded-full shadow-badge">
                  Save {discount}%
                </motion.span>
              )}
            </motion.div>

            {product.has_variants && (
              <motion.div variants={item} className="space-y-6" data-testid="variant-selector">
                {product.available_colors?.length > 0 && (
                  <ChipGroup label="Option" value={selectedColor} options={product.available_colors} onSelect={setSelectedColor} testPrefix="color"
                    isAvailable={(c) => product.variants.some((v) => v.color === c && (!product.available_sizes?.length || v.size === selectedSize) && v.stock > 0)} />
                )}
                {product.available_sizes?.length > 0 && (
                  <ChipGroup label="Size" value={selectedSize} options={product.available_sizes} onSelect={setSelectedSize} testPrefix="size"
                    isAvailable={(s) => (product.variants.find((v) => v.size === s && (!product.available_colors?.length || v.color === selectedColor))?.stock || 0) > 0} />
                )}
              </motion.div>
            )}

            <motion.div variants={item}><InfoTabs description={product.description} /></motion.div>

            <motion.div variants={item} className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <p className="text-sm font-semibold text-slate-600 mb-2">Quantity</p>
                <QuantityStepper value={quantity} onChange={setQuantity} />
              </div>
              <div className="text-right" data-testid="stock-status">
                {currentStock === 0 ? (
                  <span className="text-sm font-bold text-red-500">Out of stock</span>
                ) : currentStock < 10 ? (
                  <span className="inline-flex items-center gap-2 text-sm font-bold text-health-coral"><span className="w-2 h-2 rounded-full bg-health-coral animate-pulse" />Only {currentStock} left</span>
                ) : (
                  <span className="inline-flex items-center gap-2 text-sm font-bold text-health-emerald"><span className="w-2 h-2 rounded-full bg-health-emerald animate-pulse" />In stock, ready to ship</span>
                )}
              </div>
            </motion.div>

            <motion.div variants={item}>
              <BurstAddButton onClick={handleAddToCart} loading={addingToCart} disabled={currentStock === 0 || (product.has_variants && !selectedVariant)} />
            </motion.div>

            <motion.div variants={item}><TrustRow /></motion.div>
          </motion.div>
        </div>

        {related.length > 0 && (
          <section className="mt-24" data-testid="related-products">
            <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="font-heading text-3xl md:text-4xl font-bold text-health-ink mb-8">You might also need</motion.h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {related.map((p, i) => <HealthProductCard key={p.id} product={p} formatLocalPrice={formatLocalPrice} index={i} />)}
            </div>
          </section>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default ProductDetailPage;
